import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: 'Freight Forwarding Basics',
      questions: [
        {
          question: 'What does a freight forwarder do?',
          answer: 'A freight forwarder coordinates the movement of goods between countries, handling logistics, documentation, carrier booking, and customs coordination on behalf of importers and exporters. They act as intermediaries between shippers and transportation services, managing the entire shipping process from origin to destination.'
        },
        {
          question: 'Do I need a freight forwarder for international shipping?',
          answer: 'While not legally required, using a freight forwarder simplifies international shipping significantly. They have expertise in documentation, customs regulations, carrier relationships, and route optimization that most businesses don\'t have in-house. For businesses without dedicated logistics teams, freight forwarders are essential partners.'
        },
        {
          question: 'How does freight forwarding differ from a courier service?',
          answer: 'Freight forwarders handle commercial cargo shipments (pallets, containers, bulk goods) and coordinate complex international logistics. Courier services focus on small parcels and documents with door-to-door delivery. Freight forwarding is for business-to-business commercial cargo, while couriers serve both consumers and businesses with smaller shipments.'
        }
      ]
    },
    {
      category: 'Customs Clearance',
      questions: [
        {
          question: 'What is customs clearance?',
          answer: 'Customs clearance is the process of preparing and submitting documentation to facilitate import or export of goods. It involves ensuring compliance with customs regulations, calculating and paying duties and taxes, and coordinating with customs authorities to release cargo for entry or exit.'
        },
        {
          question: 'What is a clearing agent?',
          answer: 'A clearing agent (also called a customs broker) is a professional who handles customs clearance on behalf of importers and exporters. They prepare documentation, ensure regulatory compliance, calculate duties, and coordinate with customs authorities. In South Africa, clearing agents are essential for smooth customs processing.'
        },
        {
          question: 'How long does customs clearance take?',
          answer: 'Customs clearance timeframes vary based on cargo type, documentation accuracy, and customs workload. Typical clearance in South Africa takes 1-3 business days for standard cargo with complete documentation. Complex shipments or documentation issues can extend this timeframe. Working with an experienced clearing agent minimizes delays.'
        }
      ]
    },
    {
      category: 'Air Freight vs Ocean Freight',
      questions: [
        {
          question: 'Air freight vs sea freight: which should I choose?',
          answer: 'Air freight is faster (days) but more expensive, ideal for time-sensitive or high-value cargo. Ocean freight is more cost-effective (weeks) for larger shipments and non-urgent cargo. Choose air freight when speed is critical, and ocean freight when cost efficiency and volume matter more than transit time.'
        },
        {
          question: 'How long does air freight take from Europe to South Africa?',
          answer: 'Air freight from major European hubs (Paris, Frankfurt, Amsterdam) to South Africa (Johannesburg, Cape Town) typically takes 10-13 hours flight time. Total door-to-door transit including collection, customs clearance, and delivery is usually 3-7 business days depending on clearance speed.'
        },
        {
          question: 'How long does ocean freight take from Europe to South Africa?',
          answer: 'Ocean freight from European ports (Le Havre, Marseille, Rotterdam) to South African ports (Durban, Cape Town, Ngqura) typically takes 16-25 days sailing time. Total door-to-door transit including collection, port processing, customs clearance, and delivery is usually 4-6 weeks.'
        },
        {
          question: 'What is LCL and FCL in ocean freight?',
          answer: 'LCL (Less than Container Load) is for cargo that doesn\'t fill an entire container. Your goods share container space with other shippers\' cargo, and you pay only for the space you use. FCL (Full Container Load) is when you book an entire container (20ft or 40ft) for your exclusive cargo. FCL is more cost-effective for larger shipments.'
        }
      ]
    },
    {
      category: 'Documentation & Requirements',
      questions: [
        {
          question: 'What documents are needed for import/export shipping?',
          answer: 'Typical documents include: Commercial Invoice (detailing goods and value), Packing List (cargo details), Bill of Lading (ocean) or Airway Bill (air), Certificate of Origin, and any required import/export permits or licenses. Specific requirements vary by cargo type, origin, and destination. NETEXEC GLOBAL guides you through complete documentation requirements.'
        },
        {
          question: 'Do I need import or export permits?',
          answer: 'Permit requirements depend on the type of goods being imported or exported. Some products (food, pharmaceuticals, electronics, chemicals) require specific licenses or permits in South Africa. NETEXEC GLOBAL advises on permit requirements for your specific cargo and can guide you through the application process.'
        },
        {
          question: 'How are customs duties and taxes calculated?',
          answer: 'Customs duties are calculated based on the HS (Harmonized System) code of your goods, their declared value, and applicable tariff rates. South Africa also charges VAT on imports. Your clearing agent calculates the exact duties, taxes, and fees based on your cargo details and ensures accurate payment.'
        }
      ]
    },
    {
      category: 'Working with NETEXEC GLOBAL',
      questions: [
        {
          question: 'What information do you need for a freight quote?',
          answer: 'We need: cargo details (what, weight, dimensions), origin and destination, preferred mode (air or ocean), urgency/timeline, and whether it\'s import or export. If you have commercial invoices or packing lists, those help with accurate quoting. We\'ll provide transparent pricing and clear timelines.'
        },
        {
          question: 'How do I track my shipment?',
          answer: 'NETEXEC GLOBAL provides shipment tracking and regular updates throughout your cargo\'s journey. You\'ll receive notifications at key milestones (departure, in-transit, arrival, customs clearance) and can contact us anytime for status updates.'
        },
        {
          question: 'What areas does NETEXEC GLOBAL cover?',
          answer: 'We specialize in freight forwarding and customs clearance between South Africa, Europe, and Africa. Our core routes connect South African ports and airports with major European hubs, and we coordinate freight across the broader Europe–Africa trade corridor.'
        }
      ]
    }
  ];

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const index = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div>
      {/* Hero */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="faq-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            <span className="text-xs font-mono tracking-widest uppercase text-orange-500">Knowledge Hub</span>
            <h1 
              className="text-5xl md:text-7xl font-bold tracking-tight uppercase mt-4 mb-6"
              style={{fontFamily: 'Barlow Condensed, sans-serif'}}
            >
              Frequently Asked Questions
            </h1>
            <p className="text-xl leading-relaxed text-slate-600">
              Answers to common questions about freight forwarding, customs clearance, air freight, ocean freight, and working with NETEXEC GLOBAL.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 space-y-12">
          {faqs.map((category, categoryIndex) => (
            <div key={categoryIndex}>
              <h2 
                className="text-2xl md:text-4xl font-bold tracking-tight uppercase mb-6"
                style={{fontFamily: 'Barlow Condensed, sans-serif'}}
              >
                {category.category}
              </h2>
              <div className="space-y-4">
                {category.questions.map((faq, questionIndex) => {
                  const isOpen = openIndex === `${categoryIndex}-${questionIndex}`;
                  return (
                    <div
                      key={questionIndex}
                      className="bg-slate-50 border border-slate-200 rounded-sm overflow-hidden"
                      data-testid={`faq-${categoryIndex}-${questionIndex}`}
                    >
                      <button
                        onClick={() => toggleQuestion(categoryIndex, questionIndex)}
                        className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-slate-100 transition-colors"
                        data-testid={`faq-question-${categoryIndex}-${questionIndex}`}
                      >
                        <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 text-orange-500 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-5" data-testid={`faq-answer-${categoryIndex}-${questionIndex}`}>
                          <p className="text-base leading-relaxed text-slate-600">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 
            className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-6"
            style={{fontFamily: 'Barlow Condensed, sans-serif'}}
          >
            Still Have Questions?
          </h2>
          <p className="text-lg leading-relaxed text-slate-600 mb-8">
            Our team is here to help. Contact NETEXEC GLOBAL for personalized guidance on your freight and customs needs.
          </p>
          <Link
            to="/contact"
            data-testid="faq-cta-btn"
            className="bg-slate-900 text-white px-8 py-4 rounded-sm text-base font-medium hover:bg-orange-500 transition-colors duration-300 inline-flex items-center gap-2"
          >
            Contact Us
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FAQ;